/** Chromebook Location & Recovery: what the fleet map, a device's history, and the school's settings look like. */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type LocationCollection = 'SCHOOL_HOURS' | 'ALWAYS' | 'MISSING_ONLY';
export type LocationSource = 'GPS' | 'NETWORK' | 'ADMIN_CONSOLE' | 'MANUAL';
export type LocationDeviceStatus = 'REPORTING' | 'QUIET' | 'DARK';
export type LocationDarkReason =
  | 'NEVER_CHECKED_IN'
  | 'GUEST_MODE'
  | 'UNAFFILIATED_USER'
  | 'OFFLINE'
  | 'OUTSIDE_WINDOW'
  | 'EXCLUDED_ORG_UNIT'
  | 'LOCATION_DENIED'
  | 'ADD_ON_ENDED';
export type GeofenceKind = 'CAMPUS' | 'ALLOWED' | 'RESTRICTED';
export type LocationAlertKind = 'NO_CHECK_IN' | 'LEFT_AREA' | 'RESTRICTED_AREA' | 'OFF_CAMPUS_IN_HOURS' | 'UNEXPECTED_AREA';

export interface LocatedDevice {
  id: string;
  serialNumber: string;
  annotatedAssetId: string | null;
  orgUnitPath: string | null;
  status: LocationDeviceStatus;
  darkReason: LocationDarkReason | null;
  lastSeenAt: string | null;
  lastSource: LocationSource | null;
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastAccuracyM: number | null;
  lastUserEmail: string | null;
  lastNetworkName: string | null;
  batteryPercent: number | null;
  charging: boolean | null;
  missingAt: string | null;
  missingByName: string | null;
  missingNote: string | null;
  recoveredAt: string | null;
  enrolledAt: string | null;
  extensionVersion: string | null;
  building: { id: string; name: string } | null;
  room: { id: string; name: string } | null;
  asset: { id: string; assetTag: string; name: string | null; status: string; cartId: string | null } | null;
  googleDevice: { id: string; orgUnitPath: string | null } | null;
}

export interface LocationPoint {
  id: string;
  source: LocationSource;
  latitude: number | null;
  longitude: number | null;
  accuracyM: number | null;
  networkName: string | null;
  bssid: string | null;
  placedBy: string | null;
  userEmail: string | null;
  missing: boolean;
  batteryPercent: number | null;
  occurredAt: string;
  building: { id: string; name: string } | null;
  room: { id: string; name: string } | null;
  person: { id: string; firstName: string; lastName: string; preferredName: string | null } | null;
}

export interface DeviceHistory {
  device: LocatedDevice;
  points: LocationPoint[];
  alerts: { id: string; kind: LocationAlertKind; summary: string; firedAt: string; clearedAt: string | null; workOrderId: string | null }[];
  checkouts: { id: string; assigneeLabel: string; checkedOutAt: string; checkedInAt: string | null; dueAt: string | null; personId: string | null }[];
}

export interface FleetAnswer {
  items: LocatedDevice[];
  map: { tileUrl: string; attribution: string };
}

export interface LocationHealth {
  chromebooks: number;
  enrolled: number;
  reporting: number;
  quiet: number;
  dark: number;
  coverage: number;
  reasons: { reason: LocationDarkReason | null; count: number }[];
  missing: number;
  recoveredLast90Days: number;
  medianHoursToRecover: number | null;
  offCampusNow: number;
}

export interface LocationSettings {
  enabled: boolean;
  collection: LocationCollection;
  intervalMinutes: number;
  missingIntervalMinutes: number;
  retentionDays: number;
  quietAfterDays: number;
  consentRequired: boolean;
  consentVersion: string | null;
  lockMessage: string | null;
  alertTickets: boolean;
  alertDepartmentId: string | null;
  orgUnits: string[];
  excludedOrgUnits: string[];
  schoolHours: Record<string, [string, string][]> | null;
  enrolled: boolean;
  enrollmentTokenSetAt: string | null;
  defaultSchoolHours: Record<string, [string, string][]>;
  departments: { id: string; name: string }[];
}

export interface Geofence {
  id: string;
  name: string;
  kind: GeofenceKind;
  when: 'ANY_TIME' | 'SCHOOL_HOURS';
  buildingId: string | null;
  centerLat: number | null;
  centerLng: number | null;
  radiusM: number | null;
  polygon: [number, number][] | null;
  alertOnExit: boolean;
  alertOnEnter: boolean;
  enabled: boolean;
  building: { id: string; name: string } | null;
}

export interface MappedAccessPoint {
  id: string;
  bssid: string;
  ssid: string | null;
  label: string | null;
  source: string;
  building: { id: string; name: string } | null;
  room: { id: string; name: string } | null;
}

export const COLLECTION_LABEL: Record<LocationCollection, string> = {
  SCHOOL_HOURS: 'During the school day',
  ALWAYS: 'Whenever a device is on and online',
  MISSING_ONLY: 'Only devices flagged missing',
};

export const DARK_REASON_LABEL: Record<LocationDarkReason, string> = {
  NEVER_CHECKED_IN: 'Never checked in',
  GUEST_MODE: 'Guest session',
  UNAFFILIATED_USER: 'Signed in from outside the school',
  OFFLINE: 'Offline',
  OUTSIDE_WINDOW: 'Outside the school day',
  EXCLUDED_ORG_UNIT: 'Org unit left out',
  LOCATION_DENIED: 'Location turned off on the device',
  ADD_ON_ENDED: 'The add-on isn’t in the school’s plan',
};

export const ALERT_LABEL: Record<LocationAlertKind, string> = {
  NO_CHECK_IN: 'Hasn’t checked in',
  LEFT_AREA: 'Off campus',
  RESTRICTED_AREA: 'In a restricted area',
  OFF_CAMPUS_IN_HOURS: 'Off campus during the school day',
  UNEXPECTED_AREA: 'Somewhere unexpected',
};

export const GEOFENCE_LABEL: Record<GeofenceKind, string> = {
  CAMPUS: 'School grounds',
  ALLOWED: 'Allowed area',
  RESTRICTED: 'Restricted area',
};

/** How much to trust a fix, in words. */
export function accuracyNote(metres: number | null | undefined) {
  if (metres === null || metres === undefined) return 'Accuracy unknown';
  if (metres <= 25) return `Within ${Math.round(metres)} m`;
  if (metres <= 150) return `Within about ${Math.round(metres)} m`;
  return `Rough: within about ${Math.round(metres)} m`;
}

export const useFleet = (params: Record<string, string | undefined>, enabled = true) =>
  useQuery({ queryKey: ['device-location', 'fleet', params], queryFn: () => api.get<FleetAnswer>('/device-location/devices', params), enabled, refetchInterval: 60_000 });

export const useLocationHealth = (enabled = true) =>
  useQuery({ queryKey: ['device-location', 'health'], queryFn: () => api.get<LocationHealth>('/device-location/health'), enabled });

export const useDeviceHistory = (deviceId: string | null, days = 14) =>
  useQuery({
    queryKey: ['device-location', 'device', deviceId, days],
    queryFn: () => api.get<DeviceHistory>(`/device-location/devices/${deviceId}`, { days }),
    enabled: !!deviceId,
  });

export const useLocationSettings = (enabled = true) =>
  useQuery({ queryKey: ['device-location', 'settings'], queryFn: () => api.get<LocationSettings>('/device-location/settings'), enabled });

export const useGeofences = (enabled = true) =>
  useQuery({ queryKey: ['device-location', 'geofences'], queryFn: () => api.get<Geofence[]>('/device-location/geofences'), enabled });

export const useAccessPoints = (enabled = true) =>
  useQuery({ queryKey: ['device-location', 'access-points'], queryFn: () => api.get<MappedAccessPoint[]>('/device-location/access-points'), enabled });
